import { Card, CardContent, CardHeader, CardTitle, Spinner } from '@lensora/ui'
import { useQuery } from '@tanstack/react-query'
import * as React from 'react'


import { api } from '@/lib/api'

type TenantUsage = {
  tenantId: string
  queryCount: number
  creditsUsed: number
}

type TenantsResponse = { id: string; name: string; slug: string }[]

const PERIODS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
]

export function AdminUsagePage() {
  const [days, setDays] = React.useState(30)

  const { data: usage, isLoading: loadingUsage } = useQuery({
    queryKey: ['lensora-admin', 'usage', days],
    queryFn: () => api.get<TenantUsage[]>(`/api/lensora-admin/usage?days=${days}`),
  })

  const { data: tenants } = useQuery({
    queryKey: ['lensora-admin', 'tenants'],
    queryFn: () => api.get<TenantsResponse>('/api/lensora-admin/tenants'),
  })

  const tenantMap = React.useMemo(
    () => new Map((tenants ?? []).map((t) => [t.id, t])),
    [tenants]
  )

  const sorted = React.useMemo(
    () => [...(usage ?? [])].sort((a, b) => b.creditsUsed - a.creditsUsed),
    [usage]
  )

  const totalQueries = sorted.reduce((s, r) => s + r.queryCount, 0)
  const totalCredits = sorted.reduce((s, r) => s + r.creditsUsed, 0)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text">Usage</h1>
          <p className="mt-1 text-sm text-text-muted">AI query and credit consumption by tenant</p>
        </div>
        <div className="flex gap-1">
          {PERIODS.map(({ label, days: d }) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`rounded px-3 py-1.5 text-sm transition-colors ${
                days === d
                  ? 'bg-brand-primary text-brand-primary-foreground font-medium'
                  : 'text-text-muted hover:bg-surface-raised hover:text-text'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <p className="text-xs text-text-muted">Total queries ({days}d)</p>
            <p className="text-3xl font-bold text-text">{totalQueries.toLocaleString()}</p>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <p className="text-xs text-text-muted">Credits used ({days}d)</p>
            <p className="text-3xl font-bold text-text">{totalCredits.toLocaleString()}</p>
          </CardHeader>
        </Card>
      </div>

      {loadingUsage ? (
        <div className="flex h-40 items-center justify-center"><Spinner /></div>
      ) : sorted.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-text-muted">
            No usage data for this period.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By tenant</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="px-4 py-2 font-medium">Tenant</th>
                  <th className="px-4 py-2 font-medium text-right">Queries</th>
                  <th className="px-4 py-2 font-medium text-right">Credits used</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((row) => {
                  const t = tenantMap.get(row.tenantId)
                  return (
                    <tr key={row.tenantId} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 text-text">{t?.name ?? row.tenantId}</td>
                      <td className="px-4 py-3 text-right text-text-muted">{row.queryCount.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-text">{row.creditsUsed.toLocaleString()}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
